import React from 'react';
import { X, BarChart2, ShieldCheck, LogOut, Trophy, Star } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';

export const ProfileModal = ({ onClose }) => {
  const { user, setUser, t, getLeaderboard } = useAppContext();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isGuest');
    localStorage.removeItem('savedUserData');
    localStorage.removeItem('voiceBattleClearedByGrade');
    setUser(null); // Setting to null triggers AuthModal to appear
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 w-full max-w-lg shadow-2xl animate-in zoom-in-95 relative border border-slate-100 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 sm:top-8 right-4 sm:right-8 text-slate-400 hover:text-slate-700 bg-slate-50 p-2 rounded-full"><X size={24}/></button>

        <div className="flex flex-col items-center mb-8 sm:mb-10 mt-2 sm:mt-4">
          <div className={`w-24 h-24 sm:w-28 sm:h-28 text-white rounded-full flex items-center justify-center text-4xl sm:text-5xl font-extrabold shadow-xl mb-4 ${user.role === 'admin' ? 'bg-indigo-600' : 'bg-slate-900'}`}>
            {user.name.charAt(0).toUpperCase()}
          </div>
          <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight">{user.name}</h2>
          
          {!user.isGuest && user.role !== 'admin' && (() => {
            const leaderboard = getLeaderboard();
            const rank = leaderboard.findIndex(u => u.isCurrentUser) + 1;
            const isTop3 = rank > 0 && rank <= 3;
            return (
              <div className="flex flex-col items-center gap-3 mt-4">
                <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                  <div className="flex items-center gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-5 py-2 sm:px-6 sm:py-2.5 rounded-full font-extrabold text-sm sm:text-md shadow-md border border-orange-300">
                    <Trophy size={18} fill="currentColor" className="text-amber-100"/> {user.trophies !== undefined ? user.trophies : (user.stars || 0)} Trophies
                  </div>
                  <div className="flex items-center gap-2 bg-amber-50 text-amber-600 px-5 py-2 sm:px-6 sm:py-2.5 rounded-full font-extrabold text-sm sm:text-md border border-amber-200 shadow-inner">
                    <Star size={18} fill="currentColor"/> {user.stars || 0} Stars
                  </div>
                </div>
                <div className={`flex items-center gap-2 px-6 py-2 rounded-full font-extrabold text-sm border-2 transition-all ${isTop3 ? 'bg-gradient-to-r from-purple-100 to-fuchsia-100 text-purple-700 border-purple-300 shadow-[0_0_20px_rgba(192,38,211,0.5)] scale-110 animate-pulse' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                  <Trophy size={16} className={isTop3 ? 'text-fuchsia-500' : 'text-slate-400'}/> Server Rank: #{rank}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Guest users do not see stats */}
        {!user.isGuest && user.role !== 'admin' && (
          <>
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <BarChart2 size={16}/> Learning Statistics
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-sky-50 p-5 rounded-2xl border border-sky-100 flex flex-col justify-between h-full">
                <p className="text-sky-500 font-bold text-xs uppercase tracking-wider mb-2">{t('stat_vocab') || 'Words Learned'}</p>
                <p className="text-3xl font-extrabold text-sky-700">{Object.values(user.vocabStats || {}).filter(s => s === 'correct').length}</p>
              </div>
              <div className="bg-violet-50 p-5 rounded-2xl border border-violet-100 flex flex-col justify-between h-full">
                <p className="text-violet-500 font-bold text-xs uppercase tracking-wider mb-2">{t('stat_grammar') || 'Grammar Questions'}</p>
                <p className="text-3xl font-extrabold text-violet-700">{Object.values(user.grammarStats || {}).filter(s => s.solved).length + (user.grammar || []).length * 3}</p>
              </div>
              <div className="bg-indigo-50 p-5 rounded-2xl border border-indigo-100 flex flex-col justify-between h-full">
                <p className="text-indigo-500 font-bold text-xs uppercase tracking-wider mb-2">{t('stat_writing') || 'Writings'}</p>
                <p className="text-3xl font-extrabold text-indigo-700">{(user.completedWriting || []).length}</p>
              </div>
              <div className="bg-emerald-50 p-5 rounded-2xl border border-emerald-100 flex flex-col justify-between h-full">
                <p className="text-emerald-500 font-bold text-xs uppercase tracking-wider mb-2">{t('stat_reading') || 'Readings'}</p>
                <p className="text-3xl font-extrabold text-emerald-700">{(user.completedReading || []).length}</p>
              </div>
              <div className="bg-rose-50 p-5 rounded-2xl border border-rose-100 flex flex-col justify-between h-full">
                <p className="text-rose-500 font-bold text-xs uppercase tracking-wider mb-2">Speaking Completed</p>
                <p className="text-3xl font-extrabold text-rose-700">{(user.completedSpeaking || []).length}</p>
              </div>
              <div className="bg-amber-50 p-5 rounded-2xl border border-amber-100 flex flex-col justify-between h-full">
                <p className="text-amber-500 font-bold text-xs uppercase tracking-wider mb-2">Voice Battle Stage</p>
                <p className="text-3xl font-extrabold text-amber-700">
                  {user.clearedVoiceStages && Object.keys(user.clearedVoiceStages).length > 0 
                    ? Math.max(0, ...Object.values(user.clearedVoiceStages).map(arr => arr.length ? Math.max(...arr) + 1 : 0)) + 1 
                    : 1}
                </p>
              </div>
            </div>
          </>
        )}

        {/* Admin Confirmation Display */}
        {user.role === 'admin' && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-6 text-center mb-6">
            <ShieldCheck size={40} className="mx-auto text-indigo-500 mb-3" />
            <h3 className="font-bold text-indigo-900 text-lg mb-1">Administrator Privileges Active</h3>
            <p className="text-indigo-700 text-sm mb-4">You have full access to edit curriculum data.</p>
            <button 
              onClick={() => {
                handleLogout();
              }}
              className="px-5 py-2 bg-white text-indigo-600 font-extrabold text-sm rounded-full border border-indigo-200 hover:bg-indigo-100 transition-colors shadow-sm"
            >
              Sign out
            </button>
          </div>
        )}

        {user.isGuest ? (
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl transition-colors shadow-lg mt-4">
            <LogOut size={18}/> Sign In / Sign Up
          </button>
        ) : (
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 py-4 bg-white hover:bg-rose-50 text-rose-600 font-bold rounded-2xl transition-colors border border-slate-200 hover:border-rose-200 mt-4">
            <LogOut size={18}/> {t('btn_logout')}
          </button>
        )}
      </div>
    </div>
  );
};
